import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { Country } from '../models/country.model';
import { HttpErrorResponse } from '@angular/common/http';
import { AppointmentService } from './appointment.service';
import { Platform } from '@angular/cdk/platform';

export interface ScanRoom {
  link: string;
  room: string;
}

interface ScanData {
  room: string;
  needFacialScan: boolean;
  completed: boolean;
  omitted: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FacialScanService {
  private readonly baseApiURL = '/facialScan';
  private isDesktop = !this.platform.ANDROID && !this.platform.IOS;

  set scanData(scanData: Partial<ScanData>) {
    let storageScanData = this.scanData || {} as ScanData;
    storageScanData = { ...storageScanData, ...scanData };
    sessionStorage.setItem('scanData', JSON.stringify(storageScanData));
  }

  get scanData(): ScanData {
    const scanData = sessionStorage.getItem('scanData');
    return scanData ? JSON.parse(scanData) : {} as ScanData;
  }

  set needFacialScan(needFacialScan: boolean) {
    this.scanData = { needFacialScan };
  }

  get needFacialScan(): boolean {
    return this.scanData?.needFacialScan;
  }

  set completed(completed: boolean) {
    this.scanData = { completed };
  }

  get completed(): boolean {
    return this.scanData?.completed;
  }

  set omitted(omitted: boolean) {
    this.scanData = { omitted };
  }

  get omitted(): boolean {
    return this.scanData?.omitted;
  }

  set room(room: string) {
    this.scanData = { room };
  }

  get room(): string {
    return this.scanData?.room;
  }

  constructor(
    private platform: Platform,
    private requestService: RequestService,
    private appointmentService: AppointmentService
  ) {}

  checkNeedFacialScan(
    patientId: string,
    specialtyId: string,
    providerId: string
  ): Promise<boolean> {
    if (this.isDesktop || this.appointmentService.inAppointment()) {
      this.needFacialScan = false;
      return Promise.resolve(this.needFacialScan);
    }

    return this.requestService
      .get(`${this.baseApiURL}/needsFacialScan`, {
        patientId,
        specialtyId,
        providerId,
      })
      .then((res: { needsFacialScan: boolean }) => {
        this.needFacialScan = res?.needsFacialScan;
        return this.needFacialScan;
      })
      .catch(() => {
        this.needFacialScan = false;
        return this.needFacialScan;
      });
  }

  async createRoom(
    patientId: string,
    country?: Country
  ): Promise<ScanRoom | undefined> {
    return this.requestService
      .post(`${this.baseApiURL}`, {
        patientId,
        countryCode: country?.countryCode,
        countryId: country?.id || country,
      })
      .then((scanRoom: ScanRoom) => {
        this.room = scanRoom?.room;
        return scanRoom;
      })
      .catch((error: HttpErrorResponse) => {
        console.error(error);
        return undefined;
      });
  }

  updateScanResult(patientId: string): Promise<void> {
    this.completed = true;

    return this.requestService.put(
      `${this.baseApiURL}/${this.room}/updateResult`,
      { patientId }
    );
  }

  reset(): void {
    sessionStorage.removeItem('scanData');
  }

  isResultPending(): boolean {
    return this.needFacialScan && !this.completed && !this.omitted;
  }
}
